import { useEffect } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate,
} from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Snackbar from './components/ListSnackbar';
import Header from './components/Header';
import ListReadView from './views/ListReadView';
import ListUpdateView from './views/ListUpdateView';
import AuthorsView from './views/AuthorsView';
import AuthorReadView from './views/AuthorReadView';
import AuthorUpdateView from './views/AuthorUpdateView';
import ItemsView from './views/ItemsView';
import ItemReadView from './views/ItemReadView';
import TestView from './views/TestView';
import ListsView from './views/ListsView';
import SitesView from './views/SitesView';
import SiteReadView from './views/SiteReadView';
import SiteUpdateView from './views/SiteUpdateView';
import TagsView from './views/TagsView';
import { fetchTags } from './redux/features/tags/tagsSlice';
import { fetchUserInfo, refreshAuthToken } from './redux/features/usersSlice';
import { useTokenRefresh } from './hooks/useTokenRefresh';

// The named views, and the namespaces each one exists in.
const VIEW_SLUGS = {
  history: ['learn'],
  favorites: ['learn'],
  unknown: ['explore', 'learn', 'moderate'],
};

// Per-type URL shortcuts (e.g. /explore/podcasts) redirect to the items view
// with a `type` facet, so the active type shows as a removable chip and shares
// the single facet code path (useFacetFilters) rather than a hidden filter.
const TYPE_SLUGS = {
  articles: 'article',
  websites: 'website',
  videos: 'video',
  episodes: 'episode',
  books: 'book',
  papers: 'paper',
  discussions: 'discussion',
  tools: 'tool',
  diagrams: 'diagram',
  code: 'code',
  digests: 'digest',
  courses: 'course',
  music: 'music',
  presentations: 'presentation',
  channels: 'channel',
  podcasts: 'podcast',
};

// Brand coral ramp — the single source of truth for the primary color and its
// hover/tint shades.
const CORAL = '#F55139';
const CORAL_DARK = '#e0421f'; // pressed/hover shade -> primary.dark
const CORAL_TINT = '#fff5f3'; // faint hover background -> primary.tint

function getTheme(darkState) {
  const options = {
    components: {
      MuiTextField: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiChip: {
        defaultProps: {
          variant: 'outlined',
        },
      },
    },
  };

  const lightPalette = {
    mode: 'light',
    primary: {
      main: CORAL,
      dark: CORAL_DARK,
      tint: CORAL_TINT,
      contrastText: '#000000',
    },
    secondary: {
      main: '#3C3C3C',
    },
  };

  const darkPalette = {
    mode: 'dark',
    primary: {
      main: CORAL,
      dark: CORAL_DARK,
      tint: CORAL_TINT,
    },
    secondary: {
      main: CORAL,
    },
  };

  options.palette = darkState ? darkPalette : lightPalette;
  return createTheme(options);
}

function App() {
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.users.authToken);
  const darkMode = useSelector((state) => state.users.userInfo?.darkMode || false);
  const userInfo = useSelector((state) => state.users.userInfo);
  const theme = getTheme(darkMode);
  const isAuthenticated = !!userInfo;

  // Enable automatic token refresh
  useTokenRefresh();

  // Try to restore session on app startup using refresh token cookie
  useEffect(() => {
    const restoreSession = async () => {
      if (!authToken && !userInfo) {
        // No valid access token - try to refresh using HTTP-only cookie
        try {
          await dispatch(refreshAuthToken()).unwrap();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log('Session restoration failed:', error);
        }
      }
    };

    restoreSession();
  }, [dispatch, authToken, userInfo]);

  // Fetch user info when we have a token but no user info
  useEffect(() => {
    if (authToken && !userInfo) {
      dispatch(fetchUserInfo());
    }
  }, [authToken, userInfo, dispatch]);

  // Fetch tags when user logs in or app loads with cached auth
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchTags({ namespace: 'private' }));
    } else {
      dispatch(fetchTags({ namespace: 'public' }));
    }
  }, [isAuthenticated, dispatch]);

  const isTest = window.location && window.location.pathname === '/test';
  if (isTest) {
    return (
      <TestView />
    );
  }

  return (
    <GoogleOAuthProvider clientId="323198785229-4v5vetueh64qb7bkas8sr7gp2mepvi5k.apps.googleusercontent.com">
      <BrowserRouter>
        <DndProvider backend={HTML5Backend}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
              <Snackbar />
              <Header />
              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Routes>
                  <Route element={<ItemReadView />} path="/items/:itemId" exact />
                  <Route element={<AuthorReadView />} path="/authors/:authorId" exact />
                  <Route element={<ListReadView />} path="/lists/:listId" exact />
                  <Route element={<SiteReadView />} path="/sites/:siteId" exact />
                  <Route element={<ItemReadView namespace="pending" />} path="/moderate/items/:itemId" exact />
                  <Route element={<AuthorReadView namespace="pending" />} path="/moderate/authors/:authorId" exact />
                  <Route element={<ListReadView namespace="pending" />} path="/moderate/lists/:listId" exact />
                  <Route element={<SiteReadView namespace="pending" />} path="/moderate/sites/:siteId" exact />
                  <Route element={<Navigate to="/explore" replace />} path="/" exact />
                  <Route element={<ListsView namespace="public" />} path="/explore/lists" exact />
                  <Route element={<ItemsView namespace="public" />} path="/explore/items" exact />
                  {Object.entries(VIEW_SLUGS).flatMap(([view, namespaces]) => (
                    namespaces.map((ns) => (
                      <Route
                        key={`${ns}/${view}`}
                        path={`/${ns}/${view}`}
                        element={<Navigate to={`/${ns}/items?view=${view}`} replace />}
                      />
                    ))
                  ))}
                  {/* Per-type shortcuts (both namespaces) redirect to the items
                      view with a `type` facet; see TYPE_SLUGS. */}
                  {Object.entries(TYPE_SLUGS).flatMap(([slug, typeName]) => (
                    ['explore', 'learn', 'moderate'].map((ns) => (
                      <Route
                        key={`${ns}/${slug}`}
                        path={`/${ns}/${slug}`}
                        element={<Navigate to={`/${ns}/items?type=${typeName}`} replace />}
                      />
                    ))
                  ))}
                  <Route element={<ItemsView namespace="public" />} path="/explore" exact />
                  <Route element={<AuthorsView namespace="public" />} path="/explore/authors" exact />
                  <Route element={<ListsView />} path="/learn/lists" exact />
                  <Route element={<ListsView namespace="pending" />} path="/moderate/lists" exact />
                  <Route element={<ItemsView />} path="/learn" exact />
                  <Route element={<ItemsView />} path="/learn/items" exact />
                  <Route element={<ItemsView namespace="pending" />} path="/moderate/items" />
                  <Route element={<ItemsView namespace="public" />} path="/explore/tag/:tagId" exact />
                  <Route element={<ItemsView />} path="/learn/tag/:tagId" exact />
                  <Route element={<ItemsView view="share" />} path="/share" />
                  <Route element={<AuthorsView />} path="/learn/authors" exact />
                  <Route element={<AuthorsView namespace="pending" />} path="/moderate/authors" exact />
                  <Route element={<SitesView namespace="pending" />} path="/moderate/sites" exact />
                  <Route element={<SitesView />} path="/learn/sites" exact />
                  <Route element={<SitesView namespace="public" />} path="/explore/sites" exact />
                  <Route element={<TagsView />} path="/learn/tags" exact />
                  <Route element={<TagsView namespace="public" />} path="/explore/tags" exact />
                  <Route element={<TagsView namespace="pending" />} path="/moderate/tags" exact />
                  <Route element={<AuthorUpdateView />} path="/edit/author/:authorId" exact />
                  <Route element={<ListUpdateView />} path="/edit/list/:listId" />
                  <Route element={<SiteUpdateView />} path="/edit/site/:siteId" exact />
                </Routes>
              </Box>
            </Box>
          </ThemeProvider>
        </DndProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
